/**
 * FLAGS — registry-driven feature gating for module KINDS.
 *
 * A kind's feature is its plugin manifest's key (e.g.
 * `academy.module.forum`, `media.block.video`) — no scattered maps: adding
 * a new kind gates it automatically. Composes with the existing
 * `course_settings.enabled_module_kinds` mechanism (kind available = enabled in the
 * list AND feature effectively-enabled):
 *  · authoring's "add module" menu: disabled kinds disappear;
 *  · learning path: a disabled kind's PRE-EXISTING content
 *    shows as a "disabled by your academy" stop (data preserved),
 *    never silent disappearance.
 */
import { type Accessor, createMemo } from "solid-js";
import { featureGraph, parseFeatureOverrides, resolveFeatureEffective } from "@space/shared";
import { moduleRegistry } from "@/features/academy/modules/registry";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { useOrgSettings } from "@/features/academy/hooks/use-academy-data";
import { useCourseSettings } from "@/features/academy/hooks/use-course-modules";
import { usePlatformFlags } from "./use-feature-enabled";

export interface KindFeatureState {
  enabled: boolean;
  /** Key of the feature blocking it (for the stop's copy). */
  blockedBy?: string;
  /** The kind's feature key (from the plugin's manifest). */
  feature?: string;
}

/** Feature status per module kind, reactive to context (active org + course). */
export function useModuleKindFeatures(
  courseId: Accessor<string | undefined>,
): Accessor<(kind: string) => KindFeatureState> {
  const { activeOrgId } = useActiveOrg();
  const platform = usePlatformFlags();
  const orgSettings = useOrgSettings(() => activeOrgId());
  const courseSettings = useCourseSettings(courseId);
  const graph = featureGraph();

  const state = createMemo(() => {
    const platformMap = platform();
    const org = parseFeatureOverrides(orgSettings.data?.features);
    const course = parseFeatureOverrides(courseSettings.data?.features);
    return (kind: string): KindFeatureState => {
      const feature = moduleRegistry.get(kind)?.manifest.key;
      if (!feature) {
        return { enabled: true };
      }
      const st = resolveFeatureEffective(feature, { platformMap, org, course }, graph);
      return st.enabled
        ? { enabled: true, feature }
        : { enabled: false, feature, blockedBy: st.blockedBy };
    };
  });
  return state;
}
